import { useEffect, useState } from 'react'
import api from '@/lib/api'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { PageHeader } from '@/components/dashboard-ui'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useToast } from '@/hooks/use-toast'
import { Plus, Trash2, AlertTriangle } from 'lucide-react'

interface ProviderRow {
  id: string
  name: string
  baseUrl: string
  defaultModelId: string
  authenticationMethod: 'token' | 'basic'
  telnyxSecretIdentifier: string
  telnyxSecretId: string | null
  notes: string | null
  inUseCount: number
  createdAt: string
  updatedAt: string
}

const PRESET_PROVIDERS = [
  { name: 'OpenAI direct', baseUrl: 'https://api.openai.com/v1', model: 'gpt-5.4-mini' },
  { name: 'Z.ai (BigModel)', baseUrl: 'https://api.z.ai/api/paas/v4', model: 'glm-4.6' },
  { name: 'Moonshot (Kimi)', baseUrl: 'https://api.moonshot.ai/v1', model: 'kimi-k2-0905-preview' },
  { name: 'OpenRouter', baseUrl: 'https://openrouter.ai/api/v1', model: 'anthropic/claude-sonnet-4.6' },
  { name: 'Anthropic (via OpenAI-compat proxy)', baseUrl: '', model: 'claude-sonnet-4-6' },
]

export default function CustomLlmProvidersPage() {
  const { toast } = useToast()
  const [providers, setProviders] = useState<ProviderRow[]>([])
  const [loading, setLoading] = useState(true)
  const [createOpen, setCreateOpen] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<ProviderRow | null>(null)
  const [form, setForm] = useState({
    name: '',
    baseUrl: '',
    defaultModelId: '',
    authenticationMethod: 'token' as 'token' | 'basic',
    apiKey: '',
    notes: '',
  })

  const load = async () => {
    setLoading(true)
    try {
      const res = await api.get('/api/platform/custom-llm-providers')
      setProviders(res.data?.data || [])
    } catch (err: any) {
      toast({
        title: 'Failed to load providers',
        description: err?.response?.data?.error || err?.message || 'Unknown error',
        variant: 'destructive',
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [])

  const applyPreset = (preset: typeof PRESET_PROVIDERS[number]) => {
    setForm(prev => ({
      ...prev,
      name: preset.name,
      baseUrl: preset.baseUrl,
      defaultModelId: preset.model,
    }))
  }

  const openCreate = () => {
    setForm({
      name: '',
      baseUrl: '',
      defaultModelId: '',
      authenticationMethod: 'token',
      apiKey: '',
      notes: '',
    })
    setCreateOpen(true)
  }

  const handleSubmit = () => {
    if (!form.name || !form.baseUrl || !form.defaultModelId || !form.apiKey) {
      toast({
        title: 'Missing fields',
        description: 'Name, base URL, default model id, and API key are all required.',
        variant: 'destructive',
      })
      return
    }
    if (!/^https?:\/\//i.test(form.baseUrl)) {
      toast({
        title: 'Invalid base URL',
        description: 'Must start with http:// or https://',
        variant: 'destructive',
      })
      return
    }
    setConfirmOpen(true)
  }

  const handleConfirm = async () => {
    setSubmitting(true)
    try {
      await api.post('/api/platform/custom-llm-providers', form)
      toast({
        title: 'Provider created',
        description: `${form.name} is ready. Telnyx Integration Secret created and stored.`,
      })
      setCreateOpen(false)
      setConfirmOpen(false)
      setForm({
        name: '',
        baseUrl: '',
        defaultModelId: '',
        authenticationMethod: 'token',
        apiKey: '',
        notes: '',
      })
      await load()
    } catch (err: any) {
      toast({
        title: 'Create failed',
        description: err?.response?.data?.error || err?.message || 'Unknown error',
        variant: 'destructive',
      })
    } finally {
      setSubmitting(false)
    }
  }

  const handleDelete = async () => {
    if (!deleteTarget) return
    try {
      await api.delete(`/api/platform/custom-llm-providers/${deleteTarget.id}`)
      toast({
        title: 'Provider deleted',
        description: `${deleteTarget.name} removed. Telnyx Integration Secret also deleted.`,
      })
      setDeleteTarget(null)
      await load()
    } catch (err: any) {
      toast({
        title: 'Delete failed',
        description: err?.response?.data?.error || err?.message || 'Unknown error',
        variant: 'destructive',
      })
    }
  }

  return (
    <div className="space-y-5 sm:space-y-6">
      <PageHeader
        eyebrow="The Castle"
        title="Custom LLM Providers"
        subtitle="Configure OpenAI-compatible LLM endpoints (OpenAI direct, Z.ai, Moonshot, OpenRouter, self-hosted) that voice assistants can be routed to via Telnyx's external_llm config. API keys are stored as Telnyx Integration Secrets, never in our database."
      />

      <Card className="card-inset">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base">Configured providers</CardTitle>
              <CardDescription>
                Each row is one Telnyx Integration Secret + provider config. Assign a provider to an organization from
                its Voice Engine settings.
              </CardDescription>
            </div>
            <Button onClick={openCreate} size="sm">
              <Plus className="h-4 w-4 mr-1.5" />
              Add provider
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-8 text-center text-sm text-muted-foreground">Loading…</div>
          ) : providers.length === 0 ? (
            <div className="py-8 text-center text-sm text-muted-foreground">
              No custom providers yet. Click "Add provider" to wire one up.
            </div>
          ) : (
            <>
              {/* Desktop: dense table */}
              <div className="hidden md:block overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Base URL</TableHead>
                      <TableHead>Default model</TableHead>
                      <TableHead>Telnyx secret</TableHead>
                      <TableHead>In use</TableHead>
                      <TableHead className="w-12" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {providers.map((p) => (
                      <TableRow key={p.id}>
                        <TableCell className="font-medium">{p.name}</TableCell>
                        <TableCell className="font-mono text-xs break-all">{p.baseUrl}</TableCell>
                        <TableCell className="font-mono text-xs break-all">{p.defaultModelId}</TableCell>
                        <TableCell className="font-mono text-xs break-all">{p.telnyxSecretIdentifier}</TableCell>
                        <TableCell>
                          {p.inUseCount > 0 ? (
                            <Badge variant="secondary">{p.inUseCount} assistant{p.inUseCount === 1 ? '' : 's'}</Badge>
                          ) : (
                            <span className="text-xs text-muted-foreground">unused</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setDeleteTarget(p)}
                            disabled={p.inUseCount > 0}
                            title={p.inUseCount > 0 ? 'Reassign assistants first' : 'Delete provider'}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Mobile: stacked cards with same fields */}
              <div className="md:hidden space-y-3">
                {providers.map((p) => (
                  <div key={p.id} className="border rounded-md p-3 space-y-2 bg-card">
                    <div className="flex items-start justify-between gap-2">
                      <div className="font-medium text-sm">{p.name}</div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 shrink-0 -mt-1 -mr-1"
                        onClick={() => setDeleteTarget(p)}
                        disabled={p.inUseCount > 0}
                        title={p.inUseCount > 0 ? 'Reassign assistants first' : 'Delete provider'}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                    <div className="text-[10px] text-muted-foreground uppercase tracking-wider">Base URL</div>
                    <div className="font-mono text-xs break-all -mt-2">{p.baseUrl}</div>
                    <div className="text-[10px] text-muted-foreground uppercase tracking-wider">Default model</div>
                    <div className="font-mono text-xs break-all -mt-2">{p.defaultModelId}</div>
                    <div className="text-[10px] text-muted-foreground uppercase tracking-wider">Telnyx secret</div>
                    <div className="font-mono text-xs break-all -mt-2">{p.telnyxSecretIdentifier}</div>
                    <div>
                      {p.inUseCount > 0 ? (
                        <Badge variant="secondary">{p.inUseCount} assistant{p.inUseCount === 1 ? '' : 's'}</Badge>
                      ) : (
                        <span className="text-xs text-muted-foreground">unused</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Add provider dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add custom LLM provider</DialogTitle>
            <DialogDescription>
              Wire up an OpenAI-compatible endpoint. The API key gets stored as a Telnyx Integration Secret on save —
              we keep only the identifier.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs">Quick presets</Label>
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                {PRESET_PROVIDERS.map((preset) => (
                  <Button
                    key={preset.name}
                    variant="outline"
                    size="sm"
                    onClick={() => applyPreset(preset)}
                    className="text-xs h-7"
                  >
                    {preset.name}
                  </Button>
                ))}
              </div>
            </div>
            <div>
              <Label htmlFor="provider-name">Name</Label>
              <Input
                id="provider-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="OpenAI direct"
              />
            </div>
            <div>
              <Label htmlFor="provider-url">Base URL</Label>
              <Input
                id="provider-url"
                value={form.baseUrl}
                onChange={(e) => setForm({ ...form, baseUrl: e.target.value })}
                placeholder="https://api.openai.com/v1"
                className="font-mono text-sm"
              />
            </div>
            <div>
              <Label htmlFor="provider-model">Default model id</Label>
              <Input
                id="provider-model"
                value={form.defaultModelId}
                onChange={(e) => setForm({ ...form, defaultModelId: e.target.value })}
                placeholder="gpt-5.4-mini"
                className="font-mono text-sm"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Whatever the upstream provider expects (e.g. <code className="text-xs">gpt-5.4-mini</code>,{' '}
                <code className="text-xs">glm-4.6</code>).
              </p>
            </div>
            <div>
              <Label htmlFor="provider-auth">Authentication method</Label>
              <Select
                value={form.authenticationMethod}
                onValueChange={(v) => setForm({ ...form, authenticationMethod: v as 'token' | 'basic' })}
              >
                <SelectTrigger id="provider-auth"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="token">Bearer token (most providers)</SelectItem>
                  <SelectItem value="basic">Basic auth</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="provider-key">API key</Label>
              <Input
                id="provider-key"
                type="password"
                value={form.apiKey}
                onChange={(e) => setForm({ ...form, apiKey: e.target.value })}
                placeholder="sk-..."
                className="font-mono text-sm"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Sent once to Telnyx as an Integration Secret. We do not store it in our database.
              </p>
            </div>
            <div>
              <Label htmlFor="provider-notes">Notes (optional)</Label>
              <Textarea
                id="provider-notes"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="What this provider is for, who's using it…"
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={handleSubmit}>Continue</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Consent confirmation dialog */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-yellow-600" />
              Confirm — sending key to Telnyx
            </DialogTitle>
            <DialogDescription className="space-y-2">
              <span className="block">We're about to:</span>
              <ul className="list-disc pl-5 space-y-1 text-sm">
                <li>POST your API key to Telnyx as an Integration Secret</li>
                <li>Store only the resulting identifier in our database</li>
                <li>Make this provider available for assignment to voice assistants</li>
              </ul>
              <span className="block pt-2">
                Provider: <strong>{form.name}</strong>
                <br />
                Endpoint: <code className="text-xs bg-muted px-1 rounded">{form.baseUrl}</code>
                <br />
                Model: <code className="text-xs bg-muted px-1 rounded">{form.defaultModelId}</code>
              </span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={submitting}>
              Back
            </Button>
            <Button onClick={handleConfirm} disabled={submitting}>
              {submitting ? 'Creating…' : 'Send to Telnyx'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete provider?</DialogTitle>
            <DialogDescription>
              This will also delete the Telnyx Integration Secret associated with{' '}
              <strong>{deleteTarget?.name}</strong>. The API key cannot be recovered.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
